import React, { useState, useEffect, Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from '@/components/ui/sonner';
import { PermissionsProvider } from './contexts/PermissionsContext';
import { BrandsProvider } from './contexts/BrandsContext';
import { ThemeProvider } from './contexts/ThemeContext';
import PageLoadingSpinner from './components/PageLoadingSpinner';
import { authApi } from './lib/api';
import './App.css';

// Eager load critical pages (Login/Signup for immediate access)
import Login from './pages/Login';
import Signup from './pages/Signup';

// Lazy load all other pages for code splitting
const Dashboard = lazy(() => import('./pages/Dashboard'));
const ClientsPage = lazy(() => import('./pages/ClientsPage'));
const BrandsPage = lazy(() => import('./pages/BrandsPage'));
const BrandSyncPage = lazy(() => import('./pages/BrandSyncPage'));
const ProductsPage = lazy(() => import('./pages/ProductsPage'));
const ProductsConfigPage = lazy(() => import('./pages/ProductsConfigPage'));
const ProductCategoriesPage = lazy(() => import('./pages/ProductCategoriesPage'));
const SalesTeamPage = lazy(() => import('./pages/SalesTeamPage'));
const PriceListsPage = lazy(() => import('./pages/PriceListsPage'));
const OrdersPage = lazy(() => import('./pages/OrdersPage'));
const ProjectsPage = lazy(() => import('./pages/ProjectsPage'));
const ProjectJourneysPage = lazy(() => import('./pages/ProjectJourneysPage'));
const ProjectStrategyPage = lazy(() => import('./pages/ProjectStrategyPage'));
const ProjectBriefPage = lazy(() => import('./pages/ProjectBriefPage'));
const ProjectTriggersPage = lazy(() => import('./pages/ProjectTriggersPage'));
const ProjectTypesPage = lazy(() => import('./pages/ProjectTypesPage'));
const ProjectStepsPage = lazy(() => import('./pages/ProjectStepsPage'));
const ProjectQuestionsPage = lazy(() => import('./pages/ProjectQuestionsPage'));
const PublicWorkflowTypes = lazy(() => import('./pages/PublicWorkflowTypes'));
const PublicWorkflowSteps = lazy(() => import('./pages/PublicWorkflowSteps'));
const PublicWorkflowQuestions = lazy(() => import('./pages/PublicWorkflowQuestions'));
const AssetsPage = lazy(() => import('./pages/AssetsPage'));
const AssetTypesPage = lazy(() => import('./pages/AssetTypesPage'));
const AssetStepsPage = lazy(() => import('./pages/AssetStepsPage'));
const AssetQuestionsPage = lazy(() => import('./pages/AssetQuestionsPage'));
const AgentsPage = lazy(() => import('./pages/AgentsPage'));
const BrandIntentsPage = lazy(() => import('./pages/BrandIntentsPage'));
const AgentToolsPage = lazy(() => import('./pages/AgentToolsPage'));
const FlowsPage = lazy(() => import('./pages/FlowsPage'));
const QueuesPage = lazy(() => import('./pages/QueuesPage'));
const CustomersPage = lazy(() => import('./pages/CustomersPage'));
const LocationsPage = lazy(() => import('./pages/LocationsPage'));
const CustomerConfigPage = lazy(() => import('./pages/CustomerConfigPage'));
const CustomerSegmentsPage = lazy(() => import('./pages/CustomerSegmentsPage'));
const LogsPage = lazy(() => import('./pages/LogsPage'));
const RBACManagementPage = lazy(() => import('./pages/RBACManagementPage'));
const ProfilePage = lazy(() => import('./pages/ProfilePage'));
const ConfigPage = lazy(() => import('./pages/ConfigPage'));
const PlatformSettingsPage = lazy(() => import('./pages/PlatformSettingsPage'));
const ProvidersPage = lazy(() => import('./pages/ProvidersPage'));
const ChannelsPage = lazy(() => import('./pages/ChannelsPage'));
const ChannelTypesPage = lazy(() => import('./pages/ChannelTypesPage'));
const ChannelSettingsPage = lazy(() => import('./pages/ChannelSettingsPage'));
const BroadcastsPage = lazy(() => import('./pages/BroadcastsPage'));
const ExecutionDetailPage = lazy(() => import('./pages/ExecutionDetailPage'));
const ConversationQueuesPage = lazy(() => import('./pages/ConversationQueuesPage'));
const AgentChatPage = lazy(() => import('./pages/AgentChatPage'));
const CronJobsPage = lazy(() => import('./pages/CronJobsPage'));
const MediaActionsPage = lazy(() => import('./pages/MediaActionsPage'));
const JobsPage = lazy(() => import('./pages/JobsPage'));
const JobCategoriesPage = lazy(() => import('./pages/JobCategoriesPage'));
const TriggersPage = lazy(() => import('./pages/TriggersPage'));
const GamePage = lazy(() => import('./pages/GamePage'));
const ToolsPage = lazy(() => import('./pages/ToolsPage'));
const ToolSecretsPage = lazy(() => import('./pages/ToolSecretsPage'));
const ToolsPendingApprovalPage = lazy(() => import('./pages/ToolsPendingApprovalPage'));
const CustomerSuppressionPage = lazy(() => import('./pages/CustomerSuppressionPage'));
const ToolCategoriesPage = lazy(() => import('./pages/ToolCategoriesPage'));
const ContentTypesPage = lazy(() => import('./pages/ContentTypesPage'));
const ContentListPage = lazy(() => import('./pages/ContentListPage'));
const ContentTriggersPage = lazy(() => import('./pages/ContentTriggersPage'));
const WarehousesPage = lazy(() => import('./pages/WarehousesPage'));
const InventoryPage = lazy(() => import('./pages/InventoryPage'));
const FailedMessagesPage = lazy(() => import('./pages/FailedMessagesPage'));
const PublicEcommerce = lazy(() => import('./pages/PublicEcommerce'));
const PublicThankYou = lazy(() => import('./pages/PublicThankYou'));
const PublicBriefViewer = lazy(() => import('./pages/public-brief-viewer'));
const PublicBriefProgress = lazy(() => import('./pages/PublicBriefProgress'));
const BillingPage = lazy(() => import('./pages/BillingPage'));
const StorefrontLanding = lazy(() => import('./pages/StorefrontLanding'));

function PrivateRoute({ children }) {
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('access_token');
      if (!token) {
        setIsAuthenticated(false);
        setLoading(false);
        return;
      }

      try {
        await authApi.getCurrentUser();
        setIsAuthenticated(true);
      } catch (error) {
        setIsAuthenticated(false);
        localStorage.removeItem('access_token');
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-accent"></div>
      </div>
    );
  }

  return isAuthenticated ? children : <Navigate to="/login" replace />;
}

function App() {
  return (
    <div className="App">
      <ThemeProvider>
        <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <PermissionsProvider>
            <BrandsProvider>
              <Suspense fallback={<PageLoadingSpinner />}>
                <Routes>
                  {/* Public Routes */}
                  <Route path="/public/workflow" element={<PublicWorkflowTypes />} />
                  <Route path="/public/workflow/steps" element={<PublicWorkflowSteps />} />
                  <Route path="/public/workflow/questions" element={<PublicWorkflowQuestions />} />
                  <Route path="/public/ecommerce" element={<PublicEcommerce />} />
                  <Route path="/public/ecommerce/thank-you" element={<PublicThankYou />} />
                  <Route path="/public/thank-you" element={<PublicThankYou />} />
                  <Route path="/public/brief-viewer" element={<PublicBriefViewer />} />
                  <Route path="/public/brief-progress" element={<PublicBriefProgress />} />
                  <Route path="/tienda/:brandSlug" element={<StorefrontLanding />} />
                  
                  {/* Auth Routes - No Suspense needed (eagerly loaded) */}
                  <Route path="/login" element={<Login />} />
                  <Route path="/signup" element={<Signup />} />
            <Route
              path="/dashboard"
              element={
                <PrivateRoute>
                  <Dashboard />
                </PrivateRoute>
              }
            />
          <Route
            path="/clients"
            element={
              <PrivateRoute>
                <ClientsPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/brands"
            element={
              <PrivateRoute>
                <BrandsPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/brands/:brandId/sync"
            element={
              <PrivateRoute>
                <BrandSyncPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/products"
            element={
              <PrivateRoute>
                <ProductsPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/sales-team"
            element={
              <PrivateRoute>
                <SalesTeamPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/products/config"
            element={
              <PrivateRoute>
                <ProductsConfigPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/product-categories"
            element={
              <PrivateRoute>
                <ProductCategoriesPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/price-lists"
            element={
              <PrivateRoute>
                <PriceListsPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/price-lists/:id"
            element={
              <PrivateRoute>
                <PriceListsPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/orders"
            element={
              <PrivateRoute>
                <OrdersPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/billing"
            element={
              <PrivateRoute>
                <BillingPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/projects"
            element={
              <PrivateRoute>
                <ProjectsPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/projects/:projectId/journeys"
            element={
              <PrivateRoute>
                <ProjectJourneysPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/projects/:projectId/strategy"
            element={
              <PrivateRoute>
                <ProjectStrategyPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/projects/:projectId/brief"
            element={
              <PrivateRoute>
                <ProjectBriefPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/project-triggers"
            element={
              <PrivateRoute>
                <ProjectTriggersPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/executions/:executionId"
            element={
              <PrivateRoute>
                <ExecutionDetailPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/project-types"
            element={
              <PrivateRoute>
                <ProjectTypesPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/project-steps"
            element={
              <PrivateRoute>
                <ProjectStepsPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/project-questions"
            element={
              <PrivateRoute>
                <ProjectQuestionsPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/assets"
            element={
              <PrivateRoute>
                <AssetsPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/asset-types"
            element={
              <PrivateRoute>
                <AssetTypesPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/asset-steps"
            element={
              <PrivateRoute>
                <AssetStepsPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/asset-questions"
            element={
              <PrivateRoute>
                <AssetQuestionsPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/agents"
            element={
              <PrivateRoute>
                <AgentsPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/brand-intents"
            element={
              <PrivateRoute>
                <BrandIntentsPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/agent-tools"
            element={
              <PrivateRoute>
                <AgentToolsPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/flows"
            element={
              <PrivateRoute>
                <FlowsPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/queues"
            element={
              <PrivateRoute>
                <QueuesPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/customers"
            element={
              <PrivateRoute>
                <CustomersPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/locations"
            element={
              <PrivateRoute>
                <LocationsPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/customer-config"
            element={
              <PrivateRoute>
                <CustomerConfigPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/customer-segments"
            element={
              <PrivateRoute>
                <CustomerSegmentsPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/game"
            element={
              <PrivateRoute>
                <GamePage />
              </PrivateRoute>
            }
          />
          <Route
            path="/logs"
            element={
              <PrivateRoute>
                <LogsPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/messages/failed"
            element={
              <PrivateRoute>
                <FailedMessagesPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/providers"
            element={
              <PrivateRoute>
                <ProvidersPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/channels/:channelId/settings"
            element={
              <PrivateRoute>
                <ChannelSettingsPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/channels"
            element={
              <PrivateRoute>
                <ChannelsPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/broadcasts"
            element={
              <PrivateRoute>
                <BroadcastsPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/channel-types"
            element={
              <PrivateRoute>
                <ChannelTypesPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/conversation-queues"
            element={
              <PrivateRoute>
                <ConversationQueuesPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/agent-chat"
            element={
              <PrivateRoute>
                <AgentChatPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/cron-jobs"
            element={
              <PrivateRoute>
                <CronJobsPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/media-actions"
            element={
              <PrivateRoute>
                <MediaActionsPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/rbac"
            element={
              <PrivateRoute>
                <RBACManagementPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/profile"
            element={
              <PrivateRoute>
                <ProfilePage />
              </PrivateRoute>
            }
          />
          <Route
            path="/config"
            element={
              <PrivateRoute>
                <ConfigPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/platform-settings"
            element={
              <PrivateRoute>
                <PlatformSettingsPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/jobs"
            element={
              <PrivateRoute>
                <JobsPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/job-categories"
            element={
              <PrivateRoute>
                <JobCategoriesPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/triggers"
            element={
              <PrivateRoute>
                <TriggersPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/tools"
            element={
              <PrivateRoute>
                <ToolsPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/tool-secrets"
            element={
              <PrivateRoute>
                <ToolSecretsPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/tools/pending-approval"
            element={
              <PrivateRoute>
                <ToolsPendingApprovalPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/customer-suppression"
            element={
              <PrivateRoute>
                <CustomerSuppressionPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/tool-categories"
            element={
              <PrivateRoute>
                <ToolCategoriesPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/content-types"
            element={
              <PrivateRoute>
                <ContentTypesPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/content"
            element={
              <PrivateRoute>
                <ContentListPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/content-triggers"
            element={
              <PrivateRoute>
                <ContentTriggersPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/warehouses"
            element={
              <PrivateRoute>
                <WarehousesPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/inventory"
            element={
              <PrivateRoute>
                <InventoryPage />
              </PrivateRoute>
            }
          />
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          </Routes>
          </Suspense>
          </BrandsProvider>
        </PermissionsProvider>
      </BrowserRouter>
      </ThemeProvider>
      <Toaster position="bottom-right" />
      {process.env.REACT_APP_VERSION && (
        <div
          data-testid="version-bubble"
          style={{
            position: 'fixed',
            bottom: 12,
            right: 12,
            background: 'rgba(0,0,0,0.6)',
            color: '#fff',
            fontSize: 9,
            padding: '3px 10px',
            borderRadius: 12,
            zIndex: 9999,
            pointerEvents: 'none',
            fontFamily: 'monospace',
            letterSpacing: 0.5,
          }}
        >
          v{process.env.REACT_APP_VERSION}
        </div>
      )}
    </div>
  );
}

export default App;